import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Primitives";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";
import type { AppSetting } from "../api/types";

/**
 * Round 30 — dedicated ON/OFF controls for which sign-in methods the
 * Android app may use, on top of the same generic app_settings table
 * SettingsPage already edits (raw key/value) — this page just gives each
 * known login method its own real toggle instead of making an admin type
 * `{"value": false}` into a JSON textarea. Adding a future login method
 * (phone, email/password, Apple, …) is a one-line addition to
 * LOGIN_METHODS below; everything else — fetch, toggle, audit log — is
 * already generic.
 *
 * Enforcement is real on both ends, not just this page: routes/auth.js's
 * handleAuthExchange reads this exact same key server-side and rejects
 * the exchange outright if it's off (see that file's own comment) — this
 * page flipping the switch is not itself the security boundary, it's just
 * how the boundary's value gets set. Every save here goes through
 * /admin/settings, the same audited PATCH used everywhere else in the
 * app, super_admin-gated at the route level (see App.tsx/Sidebar.tsx).
 */
const LOGIN_METHODS: Array<{ key: string; label: string; description: string }> = [
  {
    key: "app.login_google_enabled",
    label: "Google Login",
    description: "Sign-in via Google (Android Credential Manager, exchanged for a session in /auth/exchange).",
  },
  // Future methods (phone, email/password, Apple, …) get their own entry
  // here — each is its own app_settings key, defaulting to enabled until
  // explicitly turned off, same as Google above.
];

export function LoginMethodsPage() {
  const [settings, setSettings] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const { showToast } = useToast();

  async function load() {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await apiRequest<AppSetting[]>("/admin/settings", { params: { prefix: "app.login_" } });
      const next: Record<string, boolean> = {};
      for (const method of LOGIN_METHODS) {
        const row = rows.find((r) => r.key === method.key);
        // Missing key = never configured = enabled by default, matching
        // both RemoteConfigManager's Android-side default and
        // isLoginMethodEnabled()'s server-side default.
        next[method.key] = row ? row.value !== false : true;
      }
      setSettings(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load login methods");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function toggle(key: string, label: string) {
    const nextValue = !settings[key];
    setSavingKey(key);
    try {
      await apiRequest(`/admin/settings/${encodeURIComponent(key)}`, {
        method: "PATCH",
        body: { value: nextValue, description: `Whether ${label} is available to sign in with.` },
      });
      setSettings((prev) => ({ ...prev, [key]: nextValue }));
      showToast(`${label} ${nextValue ? "enabled" : "disabled"}`, "success");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save", "error");
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <AppShell title="Login Methods">
      <p className="mb-4 max-w-lg text-xs text-ink-faint">
        Turning a method off here takes effect immediately: the app hides/disables that sign-in option, and the
        backend independently rejects any sign-in attempt using it — this is enforced server-side, not just in the
        app's UI.
      </p>

      {isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
      {error && (
        <div className="flex items-center gap-3">
          <p className="text-sm text-danger">{error}</p>
          <Button size="sm" variant="secondary" onClick={load}>
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !error && (
        <div className="max-w-xl divide-y divide-surface-border rounded-xl border border-surface-border bg-surface-raised">
          {LOGIN_METHODS.map((method) => {
            const enabled = settings[method.key] ?? true;
            return (
              <div key={method.key} className="flex items-center justify-between gap-4 px-4 py-3.5">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-ink">{method.label}</p>
                    <Badge tone={enabled ? "gold" : "neutral"}>{enabled ? "ON" : "OFF"}</Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-ink-faint">{method.description}</p>
                </div>
                <Button
                  size="sm"
                  variant={enabled ? "secondary" : "danger"}
                  isLoading={savingKey === method.key}
                  onClick={() => toggle(method.key, method.label)}
                >
                  {enabled ? "Turn Off" : "Turn On"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
